import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Form, Alert, Spinner, Badge, InputGroup, ProgressBar } from 'react-bootstrap';
import { useQuery, useMutation } from '@apollo/client';
import { useNavigate, Link } from 'react-router-dom';
import { GET_MY_PROFILE, UPDATE_MY_PROFILE, SAVE_DESIGN, DELETE_DESIGN } from '../graphql/operations';
import Footer from '../components/Footer';
import { UPLOAD_BASE_URL, UPLOAD_URL } from '../config';

const toTitleCase = (s: string) => s.replace(/\b\w/g, c => c.toUpperCase());

const InfoRow: React.FC<{ label: string; value: string; icon: string }> = ({ label, value, icon }) => (
  <div className="d-flex align-items-start gap-3 py-3 border-bottom" style={{ borderColor: 'var(--border-color) !important' }}>
    <div
      className="rounded-3 d-flex align-items-center justify-content-center flex-shrink-0"
      style={{ width: 40, height: 40, background: 'rgba(233,30,140,0.08)', color: 'var(--pink)', fontSize: 16 }}
    >
      <i className={icon}></i>
    </div>
    <div>
      <div className="text-muted" style={{ fontSize: '11.5px', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase' }}>{label}</div>
      <div className="fw-semibold text-dark mt-0.5" style={{ fontSize: '14px', lineHeight: 1.5 }}>
        {value || <span className="text-muted fst-italic" style={{ fontWeight: 400 }}>Not provided</span>}
      </div>
    </div>
  </div>
);

const ProfilePage: React.FC = () => {
  const navigate = useNavigate();

  // Auth guard
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) { navigate('/login'); }
  }, [navigate]);

  const { data, loading, error, refetch } = useQuery(GET_MY_PROFILE, { fetchPolicy: 'network-only' });
  const profile = data?.myProfile;

  // Edit mode
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    companyName: '',
    gstNumber: '',
    companyAddress: '',
    shippingAddress: '',
  });
  const [pwSection, setPwSection] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState('');

  // Populate form when profile loads
  useEffect(() => {
    if (profile) {
      setFormData({
        companyName: profile.companyName || '',
        gstNumber: profile.gstNumber || '',
        companyAddress: profile.companyAddress || '',
        shippingAddress: profile.shippingAddress || '',
      });
    }
  }, [profile]);

  const [updateProfile, { loading: saving }] = useMutation(UPDATE_MY_PROFILE);

  const [saveDesign] = useMutation(SAVE_DESIGN);
  const [deleteDesign] = useMutation(DELETE_DESIGN);
  const [uploadingDesign, setUploadingDesign] = useState(false);
  const [uploadDesignProgress, setUploadDesignProgress] = useState(0);

  const handleDesignUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingDesign(true);
    setUploadDesignProgress(0);

    const formData = new FormData();
    formData.append('designFile', file);

    try {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', UPLOAD_URL, true);

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const percent = Math.round((event.loaded / event.total) * 100);
          setUploadDesignProgress(percent);
        }
      };

      xhr.onload = async () => {
        if (xhr.status === 200) {
          const response = JSON.parse(xhr.responseText);
          if (!response.success) {
            setUploadingDesign(false);
            setSaveError(response.message || 'Upload failed');
            return;
          }
          const uploadedFileName = response.filename;
          
          await saveDesign({
            variables: {
              fileName: uploadedFileName,
              friendlyName: file.name
            },
            refetchQueries: [{ query: GET_MY_PROFILE }]
          });
          setUploadingDesign(false);
          setSaveSuccess('Design uploaded successfully!');
        } else {
          setUploadingDesign(false);
          setSaveError('Upload failed: ' + xhr.responseText);
        }
      };

      xhr.onerror = () => {
        setUploadingDesign(false);
        setSaveError('Network error during upload.');
      };

      xhr.send(formData);
    } catch (err: any) {
      setUploadingDesign(false);
      setSaveError('Error: ' + err.message);
    }
  };

  const handleDeleteDesign = async (designId: string) => {
    if (!window.confirm('Are you sure you want to delete this saved design?')) return;
    try {
      await deleteDesign({
        variables: { designId },
        refetchQueries: [{ query: GET_MY_PROFILE }]
      });
      setSaveSuccess('Design deleted successfully!');
    } catch (err: any) {
      setSaveError('Failed to delete design: ' + err.message);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaveError('');
    setSaveSuccess('');

    if (pwSection && newPassword !== confirmNewPassword) {
      setSaveError('New passwords do not match.');
      return;
    }
    if (pwSection && newPassword && newPassword.length < 6) {
      setSaveError('New password must be at least 6 characters.');
      return;
    }

    try {
      const vars: any = { ...formData };
      if (pwSection && newPassword) {
        vars.currentPassword = currentPassword;
        vars.newPassword = newPassword;
      }
      const { data: res } = await updateProfile({ variables: vars });
      if (res?.updateMyProfile?.success) {
        setSaveSuccess('Profile updated successfully!');
        // Sync localStorage
        const u = res.updateMyProfile.user;
        localStorage.setItem('companyName', u.companyName || '');
        localStorage.setItem('gstNumber', u.gstNumber || '');
        localStorage.setItem('companyAddress', u.companyAddress || '');
        localStorage.setItem('shippingAddress', u.shippingAddress || '');
        setEditing(false);
        setPwSection(false);
        setCurrentPassword(''); setNewPassword(''); setConfirmNewPassword('');
        refetch();
      } else {
        setSaveError(res?.updateMyProfile?.message || 'Update failed. Please try again.');
      }
    } catch (err: any) {
      setSaveError(err.message || 'Update failed.');
    }
  };

  const username = toTitleCase(localStorage.getItem('username') || 'Customer');
  const initials = (localStorage.getItem('username') || 'C').slice(0, 2).toUpperCase();

  const memberSince = profile?.createdAt
    ? new Date(Number(profile.createdAt) || profile.createdAt).toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' })
    : '—';

  return (
    <div className="page-fade d-flex flex-column" style={{ minHeight: 'calc(100vh - 68px)', backgroundColor: 'var(--bg-secondary)' }}>
      <div className="flex-grow-1 py-5">
        <Container>

          {/* Header */}
          <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
            <div>
              <span className="text-primary fw-bold text-uppercase small" style={{ letterSpacing: '1px' }}>Account</span>
              <h2 className="fw-bold text-dark mb-1">My Profile</h2>
              <p className="text-muted mb-0" style={{ fontSize: '13.5px' }}>Manage your billing info, shipping address, and account settings.</p>
            </div>
            <div className="d-flex gap-2">
              {localStorage.getItem('role') === 'admin' ? (
                <Link to="/admin" className="btn btn-outline-secondary fw-semibold px-3 py-2 d-flex align-items-center gap-2" style={{ borderRadius: '8px', fontSize: '13.5px' }}>
                  <i className="fas fa-tachometer-alt"></i> Admin Dashboard
                </Link>
              ) : (
                <Link to="/my-orders" className="btn btn-outline-secondary fw-semibold px-3 py-2 d-flex align-items-center gap-2" style={{ borderRadius: '8px', fontSize: '13.5px' }}>
                  <i className="fas fa-shopping-bag"></i> My Orders
                </Link>
              )}
              <button
                className="btn btn-outline-danger fw-semibold px-3 py-2 d-flex align-items-center gap-2"
                style={{ borderRadius: '8px', fontSize: '13.5px' }}
                onClick={() => { localStorage.clear(); navigate('/login'); window.location.reload(); }}
              >
                <i className="fas fa-sign-out-alt"></i> Logout
              </button>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3 text-muted">Loading your profile...</p>
            </div>
          ) : error ? (
            <Alert variant="danger" className="border-0 rounded-4">Failed to load profile: {error.message}</Alert>
          ) : (
            <Row className="g-4">

              {/* Left Column – Avatar & Identity Card */}
              <Col xs={12} lg={4}>
                <Card className="border-0 shadow-sm rounded-4 bg-white h-100">
                  <Card.Body className="p-4 d-flex flex-column align-items-center text-center">
                    {/* Avatar */}
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center fw-bold mb-3"
                      style={{
                        width: 90, height: 90, fontSize: 32,
                        background: 'linear-gradient(135deg, #e91e8c 0%, #3b5bdb 100%)',
                        color: '#fff',
                        boxShadow: '0 8px 24px rgba(233,30,140,0.25)',
                      }}
                    >
                      {initials}
                    </div>
                    <h4 className="fw-bold text-dark mb-1">{username}</h4>
                    <Badge bg="light" text="dark" className="mb-2 px-3 py-1.5 rounded-pill border" style={{ fontSize: '11px' }}>
                      <i className={`fas fa-${localStorage.getItem('role') === 'admin' ? 'shield-alt' : 'user'} me-1`} style={{ color: 'var(--pink)' }}></i>
                      {localStorage.getItem('role') === 'admin' ? 'Administrator' : 'Customer'}
                    </Badge>
                    
                    {profile?.customerId && (
                      <div className="text-muted fw-bold mb-3 font-monospace d-flex align-items-center gap-2" style={{ fontSize: '12.5px', background: '#f8f9fa', padding: '6px 12px', borderRadius: '8px', border: '1px dashed #ced4da' }}>
                        <i className="fas fa-id-card text-secondary"></i> {profile.customerId}
                      </div>
                    )}

                    <div className="w-100 border-top pt-3 mt-1">
                      <div className="d-flex justify-content-between align-items-center py-2">
                        <span className="text-muted small">Member Since</span>
                        <span className="fw-semibold text-dark small">{memberSince}</span>
                      </div>
                      {profile?.gstNumber && (
                        <div className="d-flex justify-content-between align-items-center py-2 border-top">
                          <span className="text-muted small">GST Number</span>
                          <span className="fw-semibold text-dark font-monospace small">{profile.gstNumber}</span>
                        </div>
                      )}
                      <div className="d-flex justify-content-between align-items-center py-2 border-top">
                        <span className="text-muted small">Username</span>
                        <span className="fw-semibold text-dark small font-monospace">{profile?.username}</span>
                      </div>
                    </div>

                    <Button
                      variant={editing ? 'outline-secondary' : 'primary'}
                      className="w-100 mt-4 fw-semibold py-2 d-flex align-items-center justify-content-center gap-2"
                      style={{
                        borderRadius: '10px', fontSize: '13.5px',
                        background: editing ? undefined : 'linear-gradient(90deg, #e91e8c, #3b5bdb)',
                        border: editing ? undefined : 'none',
                      }}
                      onClick={() => { setEditing(!editing); setSaveError(''); setSaveSuccess(''); }}
                    >
                      <i className={editing ? 'fas fa-times' : 'fas fa-edit'}></i>
                      {editing ? 'Cancel Editing' : 'Edit Profile'}
                    </Button>
                  </Card.Body>
                </Card>
              </Col>

              {/* Right Column – Details / Edit Form */}
              <Col xs={12} lg={8}>
                {saveSuccess && (
                  <Alert variant="success" className="border-0 rounded-4 mb-4 d-flex align-items-center gap-2 py-2.5" style={{ fontSize: '13px' }} onClose={() => setSaveSuccess('')} dismissible>
                    <i className="fas fa-check-circle"></i> {saveSuccess}
                  </Alert>
                )}

                {!editing ? (
                  /* ─── View Mode ─── */
                  <>
                    <Card className="border-0 shadow-sm rounded-4 bg-white mb-4">
                      <Card.Body className="p-4">
                        <h5 className="fw-bold text-dark mb-3 d-flex align-items-center gap-2">
                          <i className="fas fa-building" style={{ color: 'var(--pink)', fontSize: 18 }}></i>
                          Business Information
                        </h5>
                        <InfoRow icon="fas fa-building" label="Company Name" value={profile?.companyName || ''} />
                        <InfoRow icon="fas fa-percent" label="GST Number" value={profile?.gstNumber || ''} />
                      </Card.Body>
                    </Card>

                    <Card className="border-0 shadow-sm rounded-4 bg-white mb-4">
                      <Card.Body className="p-4">
                        <h5 className="fw-bold text-dark mb-3 d-flex align-items-center gap-2">
                          <i className="fas fa-map-marked-alt" style={{ color: 'var(--pink)', fontSize: 18 }}></i>
                          Addresses
                        </h5>
                        <InfoRow icon="fas fa-file-invoice" label="Billing / Company Address" value={profile?.companyAddress || ''} />
                        <div style={{ borderBottom: 'none' }}>
                          <InfoRow icon="fas fa-truck" label="Shipping / Delivery Address" value={profile?.shippingAddress || ''} />
                        </div>
                      </Card.Body>
                    </Card>

                    <Card className="border-0 shadow-sm rounded-4 bg-white">
                      <Card.Body className="p-4">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <h5 className="fw-bold text-dark mb-0 d-flex align-items-center gap-2">
                            <i className="fas fa-images" style={{ color: 'var(--pink)', fontSize: 18 }}></i>
                            My Saved Designs
                          </h5>
                          <div>
                            <input type="file" id="designUpload" className="d-none" onChange={handleDesignUpload} />
                            <Button 
                              variant="outline-primary" 
                              size="sm" 
                              className="fw-semibold rounded-3"
                              onClick={() => document.getElementById('designUpload')?.click()}
                              disabled={uploadingDesign}
                            >
                              {uploadingDesign ? (
                                <><Spinner animation="border" size="sm" className="me-2" /> Uploading...</>
                              ) : (
                                <><i className="fas fa-upload me-1"></i> Upload New</>
                              )}
                            </Button>
                          </div>
                        </div>
                        
                        {uploadingDesign && uploadDesignProgress > 0 && (
                          <div className="mb-3">
                            <ProgressBar now={uploadDesignProgress} label={`${uploadDesignProgress}%`} />
                          </div>
                        )}

                        {profile?.savedDesigns && profile.savedDesigns.length > 0 ? (
                          <Row className="g-3">
                            {profile.savedDesigns.map((design: any) => (
                              <Col xs={12} sm={6} md={4} key={design.id}>
                                <div className="border rounded-3 p-2 position-relative bg-light h-100 d-flex flex-column shadow-sm">
                                  <Button 
                                    variant="danger" 
                                    size="sm" 
                                    className="position-absolute top-0 end-0 m-1 rounded-circle d-flex align-items-center justify-content-center"
                                    style={{ width: '28px', height: '28px', padding: 0, zIndex: 10, boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }}
                                    onClick={() => handleDeleteDesign(design.id)}
                                  >
                                    <i className="fas fa-times"></i>
                                  </Button>
                                  <div className="text-center mb-2 flex-grow-1 d-flex align-items-center justify-content-center bg-white rounded border overflow-hidden" style={{ minHeight: '120px' }}>
                                    {design.fileName.match(/\.(jpeg|jpg|gif|png|webp)$/i) ? (
                                      <img src={`${UPLOAD_BASE_URL}/${design.fileName}`} alt={design.friendlyName} style={{ maxWidth: '100%', maxHeight: '120px', objectFit: 'contain' }} />
                                    ) : (
                                      <div className="text-center py-2">
                                        <i className="fas fa-file-alt text-secondary mb-1" style={{ fontSize: '32px' }}></i>
                                        <div className="text-muted" style={{fontSize: '10px'}}>{design.fileName.split('.').pop()?.toUpperCase()}</div>
                                      </div>
                                    )}
                                  </div>
                                  <div className="text-truncate fw-semibold text-dark small text-center" title={design.friendlyName}>
                                    {design.friendlyName}
                                  </div>
                                  <div className="text-muted text-center" style={{ fontSize: '10px' }}>
                                    {new Date(Number(design.uploadedAt)).toLocaleDateString()}
                                  </div>
                                </div>
                              </Col>
                            ))}
                          </Row>
                        ) : (
                          <div className="text-center py-4 text-muted border rounded-3 bg-light border-dashed">
                            <i className="fas fa-folder-open mb-2" style={{ fontSize: '24px', opacity: 0.5 }}></i>
                            <p className="mb-0 small">No saved designs yet. Upload one above to use in future orders.</p>
                          </div>
                        )}
                      </Card.Body>
                    </Card>
                  </>
                ) : (
                  /* ─── Edit Mode ─── */
                  <Card className="border-0 shadow-sm rounded-4 bg-white">
                    <Card.Body className="p-4">
                      <h5 className="fw-bold text-dark mb-4 d-flex align-items-center gap-2">
                        <i className="fas fa-edit" style={{ color: 'var(--pink)', fontSize: 18 }}></i>
                        Edit Your Details
                      </h5>

                      {saveError && (
                        <Alert variant="danger" className="border-0 rounded-3 mb-4 d-flex align-items-center gap-2 py-2.5" style={{ fontSize: '13px' }}>
                          <i className="fas fa-exclamation-triangle"></i> {saveError}
                        </Alert>
                      )}

                      <Form onSubmit={handleSave}>
                        <Row className="g-3 mb-4">
                          <Col md={6}>
                            <Form.Group controlId="editCompanyName">
                              <Form.Label className="fw-semibold text-dark mb-1" style={{ fontSize: '12.5px' }}>
                                <i className="fas fa-building me-1 text-muted"></i> Company Name
                              </Form.Label>
                              <Form.Control
                                type="text"
                                className="rounded-3 py-2"
                                style={{ fontSize: '13.5px', borderColor: '#e2e8f0' }}
                                placeholder="e.g. Acme Corp"
                                value={formData.companyName}
                                onChange={e => setFormData(p => ({ ...p, companyName: e.target.value }))}
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group controlId="editGst">
                              <Form.Label className="fw-semibold text-dark mb-1" style={{ fontSize: '12.5px' }}>
                                <i className="fas fa-percent me-1 text-muted"></i> GST Number
                              </Form.Label>
                              <Form.Control
                                type="text"
                                className="rounded-3 py-2 font-monospace"
                                style={{ fontSize: '13.5px', borderColor: '#e2e8f0' }}
                                placeholder="15-digit GSTIN"
                                value={formData.gstNumber}
                                onChange={e => setFormData(p => ({ ...p, gstNumber: e.target.value.toUpperCase() }))}
                                maxLength={15}
                              />
                            </Form.Group>
                          </Col>
                          <Col md={12}>
                            <Form.Group controlId="editBillingAddr">
                              <Form.Label className="fw-semibold text-dark mb-1" style={{ fontSize: '12.5px' }}>
                                <i className="fas fa-file-invoice me-1 text-muted"></i> Billing / Company Address
                              </Form.Label>
                              <Form.Control
                                as="textarea"
                                rows={2}
                                className="rounded-3 py-2"
                                style={{ fontSize: '13.5px', borderColor: '#e2e8f0', resize: 'none' }}
                                placeholder="Registered billing address"
                                value={formData.companyAddress}
                                onChange={e => setFormData(p => ({ ...p, companyAddress: e.target.value }))}
                              />
                            </Form.Group>
                          </Col>
                          <Col md={12}>
                            <Form.Group controlId="editShippingAddr">
                              <Form.Label className="fw-semibold text-dark mb-1" style={{ fontSize: '12.5px' }}>
                                <i className="fas fa-truck me-1 text-muted"></i> Shipping / Delivery Address
                              </Form.Label>
                              <Form.Control
                                as="textarea"
                                rows={2}
                                className="rounded-3 py-2"
                                style={{ fontSize: '13.5px', borderColor: '#e2e8f0', resize: 'none' }}
                                placeholder="Where should deliveries be sent?"
                                value={formData.shippingAddress}
                                onChange={e => setFormData(p => ({ ...p, shippingAddress: e.target.value }))}
                              />
                            </Form.Group>
                          </Col>
                        </Row>

                        {/* Change Password accordion */}
                        <div
                          className="rounded-3 border p-3 mb-4"
                          style={{ borderColor: '#e2e8f0', backgroundColor: 'var(--bg-secondary)' }}
                        >
                          <button
                            type="button"
                            className="btn btn-link p-0 fw-semibold text-decoration-none d-flex align-items-center gap-2"
                            style={{ fontSize: '13.5px', color: 'var(--pink)' }}
                            onClick={() => setPwSection(!pwSection)}
                          >
                            <i className={`fas fa-chevron-${pwSection ? 'up' : 'down'}`} style={{ fontSize: '11px' }}></i>
                            {pwSection ? 'Cancel Password Change' : 'Change Password'}
                          </button>

                          {pwSection && (
                            <Row className="g-3 mt-2">
                              <Col md={12}>
                                <Form.Group controlId="editCurrentPw">
                                  <Form.Label className="fw-semibold text-dark mb-1" style={{ fontSize: '12px' }}>Current Password</Form.Label>
                                  <InputGroup className="border rounded-3 overflow-hidden" style={{ borderColor: '#e2e8f0' }}>
                                    <Form.Control
                                      type={showPw ? 'text' : 'password'}
                                      className="border-0 py-2"
                                      style={{ fontSize: '13.5px', boxShadow: 'none' }}
                                      placeholder="Enter current password"
                                      value={currentPassword}
                                      onChange={e => setCurrentPassword(e.target.value)}
                                    />
                                    <Button variant="link" className="border-0 text-muted px-3 bg-white" onClick={() => setShowPw(!showPw)} style={{ boxShadow: 'none' }}>
                                      <i className={showPw ? 'far fa-eye-slash' : 'far fa-eye'}></i>
                                    </Button>
                                  </InputGroup>
                                </Form.Group>
                              </Col>
                              <Col md={6}>
                                <Form.Group controlId="editNewPw">
                                  <Form.Label className="fw-semibold text-dark mb-1" style={{ fontSize: '12px' }}>New Password</Form.Label>
                                  <Form.Control
                                    type={showPw ? 'text' : 'password'}
                                    className="rounded-3 py-2"
                                    style={{ fontSize: '13.5px', borderColor: '#e2e8f0' }}
                                    placeholder="Min 6 characters"
                                    value={newPassword}
                                    onChange={e => setNewPassword(e.target.value)}
                                  />
                                </Form.Group>
                              </Col>
                              <Col md={6}>
                                <Form.Group controlId="editConfirmPw">
                                  <Form.Label className="fw-semibold text-dark mb-1" style={{ fontSize: '12px' }}>Confirm New Password</Form.Label>
                                  <Form.Control
                                    type={showPw ? 'text' : 'password'}
                                    className="rounded-3 py-2"
                                    style={{ fontSize: '13.5px', borderColor: '#e2e8f0' }}
                                    placeholder="Repeat new password"
                                    value={confirmNewPassword}
                                    onChange={e => setConfirmNewPassword(e.target.value)}
                                  />
                                </Form.Group>
                              </Col>
                            </Row>
                          )}
                        </div>

                        <div className="d-flex gap-2 justify-content-end flex-wrap">
                          <Button
                            variant="outline-secondary"
                            className="fw-semibold px-4 py-2"
                            style={{ borderRadius: '10px', fontSize: '13.5px' }}
                            onClick={() => { setEditing(false); setSaveError(''); }}
                          >
                            Cancel
                          </Button>
                          <Button
                            type="submit"
                            disabled={saving}
                            className="fw-semibold px-5 py-2 text-white d-flex align-items-center gap-2"
                            style={{
                              borderRadius: '10px', fontSize: '13.5px',
                              background: 'linear-gradient(90deg, #e91e8c, #3b5bdb)',
                              border: 'none',
                            }}
                          >
                            {saving ? <><i className="fas fa-spinner fa-spin"></i> Saving...</> : <><i className="fas fa-save"></i> Save Changes</>}
                          </Button>
                        </div>
                      </Form>
                    </Card.Body>
                  </Card>
                )}
              </Col>
            </Row>
          )}
        </Container>
      </div>
      <Footer />
    </div>
  );
};

export default ProfilePage;
